import { useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { createClient } from '@/util/supabase/component'
import { type Species } from '@/types'
import { useQuery } from '@tanstack/react-query'
import { speciesLookupQueryFn } from './queries'

// Type definition for the form data
interface SpeciesFormData {
  family: number
  genus: string
  epithet: string
  infraspecies_epithet: string | null
  authority: string | null
  infraspecies_rank: string | null
  infraspecies_authority: string | null
  synonyms: string | null
  common_name: string | null
}

// Type for family data
interface Family {
  id: number
  name: string
}

const trimString = yup
  .string()
  .nullable()
  .transform((value) => (value ? value.trim() : null))
  .defined()

// Validation schema
const speciesSchema = yup.object().shape({
  family: yup.number().required(),
  genus: yup.string().trim().required('Genus is required'),
  epithet: yup.string().trim().required('Epithet is required'),
  infraspecies_epithet: trimString,
  authority: trimString,
  infraspecies_rank: trimString,
  infraspecies_authority: trimString,
  synonyms: trimString,
  common_name: trimString,
})

const supabase = createClient()

const getUniqueSpeciesName = (data: Species | SpeciesFormData) => {
  const resultKeys = [
    'genus',
    'epithet',
    'infraspecies_rank',
    'infraspecies_epithet',
  ] as const

  return resultKeys
    .map((key) => data[key])
    .filter(Boolean)
    .join(' ')
}

export const SpeciesForm: React.FC = () => {
  const [families, setFamilies] = useState<Family[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [result, setResult] = useState<Species>()
  const [error, setError] = useState<string | null>(null)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<SpeciesFormData>({
    resolver: yupResolver(speciesSchema),
    defaultValues: {
      family: undefined,
      genus: '',
      epithet: '',
      infraspecies_epithet: null,
      authority: null,
      infraspecies_rank: null,
      infraspecies_authority: null,
      synonyms: null,
      common_name: null,
    },
  })

  // Fetch families for dropdown
  useEffect(() => {
    const fetchFamilies = async () => {
      try {
        setLoading(true)
        const { data, error } = await supabase
          .from('families')
          .select('id, name')
          .order('name')

        if (error) throw error
        setFamilies(data || [])
      } catch (err) {
        console.error('Error fetching families:', err)
        setError('Failed to load families')
      } finally {
        setLoading(false)
      }
    }

    if (!families.length) fetchFamilies()
  }, [families])

  const formValues = watch()

  const { data: existingSpecies } = useQuery({
    queryKey: [
      'species',
      `${formValues.genus} ${formValues.epithet} ${formValues.infraspecies_rank ?? ''} ${formValues.infraspecies_epithet ?? ''}`.trim(),
    ],
    queryFn: speciesLookupQueryFn,
  })

  const onSubmit = async (data: SpeciesFormData) => {
    try {
      setLoading(true)
      setError(null)

      // first check if the species already exists
      if (existingSpecies && existingSpecies.length > 0) {
        setError(
          `Species already exists. Please use the following taxon name to add data to SID: ${getUniqueSpeciesName(data)}`,
        )
        return
      }
      // Insert new species record
      const { error: insertError, data: species } = await supabase
        .from('species')
        .insert([data])
        .select('*')

      if (insertError) throw insertError
      if (species.length < 1) throw new Error('No species returned')
      setResult(species[0] as Species)
      // Reset form
      reset()
    } catch (err) {
      console.error('Error creating species:', err)
      setError('Failed to create species')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-2xl mx-auto p-4 bg-white rounded-lg">
      {result && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
          <p>Species created successfully!</p>
          <p>
            Please use the following taxon name to add data to SID:
            <span className="font-mono text-sm text-gray-600 px-1">
              {getUniqueSpeciesName(result)}
            </span>
          </p>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{error}</div>
      )}
      {!result && (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Family Select */}
          <div>
            <label
              htmlFor="family"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Family
            </label>
            <Controller
              name="family"
              control={control}
              render={({ field }) => (
                <select
                  {...field}
                  value={field.value ?? ''}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value ? Number(e.target.value) : null,
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  disabled={loading || isSubmitting}
                >
                  <option value="">Select Family</option>
                  {families.map((family) => (
                    <option key={family.id} value={family.id}>
                      {family.name}
                    </option>
                  ))}
                </select>
              )}
            />
            {errors.family && (
              <p className="mt-1 text-sm text-red-600">
                {errors.family.message}
              </p>
            )}
          </div>

          {/* Genus */}
          <div>
            <label
              htmlFor="genus"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Genus *
            </label>
            <Controller
              name="genus"
              control={control}
              render={({ field }) => (
                <input
                  type="text"
                  {...field}
                  autoComplete="off"
                  autoCorrect="off"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  disabled={loading || isSubmitting}
                />
              )}
            />
            {errors.genus && (
              <p className="mt-1 text-sm text-red-600">
                {errors.genus.message}
              </p>
            )}
          </div>

          {/* Epithet */}
          <div>
            <label
              htmlFor="epithet"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Epithet *
            </label>
            <Controller
              name="epithet"
              control={control}
              render={({ field }) => (
                <input
                  type="text"
                  {...field}
                  autoComplete="off"
                  autoCapitalize="off"
                  autoCorrect="off"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  disabled={loading || isSubmitting}
                />
              )}
            />
            {errors.epithet && (
              <p className="mt-1 text-sm text-red-600">
                {errors.epithet.message}
              </p>
            )}
          </div>

          {/* Authority */}
          <div>
            <label
              htmlFor="authority"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Authority
            </label>
            <Controller
              name="authority"
              control={control}
              render={({ field: { value, onChange, ...field } }) => (
                <input
                  type="text"
                  value={value ?? ''}
                  onChange={(e) => onChange(e.target.value || null)}
                  {...field}
                  autoComplete="off"
                  autoCorrect="off"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  disabled={loading || isSubmitting}
                />
              )}
            />
            {errors.authority && (
              <p className="mt-1 text-sm text-red-600">
                {errors.authority.message}
              </p>
            )}
          </div>

          {/* Infraspecies section */}
          <div className="border-t pt-6 border-gray-200">
            <h3 className="text-lg font-medium mb-4">
              Infraspecies Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Infraspecies Rank */}
              <div>
                <label
                  htmlFor="infraspecies_rank"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Infraspecies Rank
                </label>
                <Controller
                  name="infraspecies_rank"
                  control={control}
                  render={({ field: { value, onChange, ...field } }) => (
                    <input
                      type="text"
                      value={value ?? ''}
                      onChange={(e) => onChange(e.target.value || null)}
                      {...field}
                      autoComplete="off"
                      autoCorrect="off"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      disabled={loading || isSubmitting}
                    />
                  )}
                />
              </div>

              {/* Infraspecies Epithet */}
              <div>
                <label
                  htmlFor="infraspecies_epithet"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Infraspecies Epithet
                </label>
                <Controller
                  name="infraspecies_epithet"
                  control={control}
                  render={({ field: { value, onChange, ...field } }) => (
                    <input
                      type="text"
                      value={value ?? ''}
                      onChange={(e) => onChange(e.target.value || null)}
                      {...field}
                      autoComplete="off"
                      autoCorrect="off"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      disabled={loading || isSubmitting}
                    />
                  )}
                />
              </div>

              {/* Infraspecies Authority */}
              <div>
                <label
                  htmlFor="infraspecies_authority"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Infraspecies Authority
                </label>
                <Controller
                  name="infraspecies_authority"
                  control={control}
                  render={({ field: { value, onChange, ...field } }) => (
                    <input
                      type="text"
                      value={value ?? ''}
                      onChange={(e) => onChange(e.target.value || null)}
                      {...field}
                      autoComplete="off"
                      autoCorrect="off"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      disabled={loading || isSubmitting}
                    />
                  )}
                />
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="border-t pt-6 border-gray-200">
            <h3 className="text-lg font-medium mb-4">Additional Information</h3>

            <div className="flex flex-col gap-4">
              {/* Common Name */}
              <div>
                <label
                  htmlFor="common_name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Common Name
                </label>
                <Controller
                  name="common_name"
                  control={control}
                  render={({ field: { value, onChange, ...field } }) => (
                    <input
                      type="text"
                      value={value ?? ''}
                      onChange={(e) => onChange(e.target.value || null)}
                      {...field}
                      autoComplete="off"
                      autoCorrect="off"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      disabled={loading || isSubmitting}
                    />
                  )}
                />
              </div>

              {/* Synonyms */}
              <div className="mt-4">
                <label
                  htmlFor="synonyms"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Synonyms
                </label>
                <Controller
                  name="synonyms"
                  control={control}
                  render={({ field: { value, onChange, ...field } }) => (
                    <textarea
                      value={value ?? ''}
                      onChange={(e) => onChange(e.target.value || null)}
                      {...field}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      disabled={loading || isSubmitting}
                    />
                  )}
                />
              </div>
            </div>
          </div>

          <div className="pt-4">
            <button
              type="submit"
              className="w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-300"
              disabled={loading || isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save Species'}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}
